import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Logo from '../../images/MainLogo_Big.png';
import AntsBg from '../../images/background_big.png';
import { siblingOrigin, isTrustedOrigin } from '../../utils/siblingSite';

const LoginWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #0F263B 0%, #0a1929 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${AntsBg}) no-repeat center 30%;
    background-size: cover;
    opacity: 0.15;
    pointer-events: none;
  }
`;

const LoginBox = styled.div`
  background: rgba(15, 38, 59, 0.95);
  border: 1px solid rgba(1, 188, 214, 0.3);
  border-radius: 20px;
  padding: 48px 40px;
  width: 420px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5),
              0 0 40px rgba(1, 188, 214, 0.1);
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  text-align: center;
`;

const LogoImg = styled.img`
  display: block;
  margin: 0 auto 24px;
  height: 56px;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3));
`;

const Title = styled.h1`
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
  letter-spacing: 1px;
`;

const SubTitle = styled.p`
  text-align: center;
  font-size: 13px;
  color: rgba(255,255,255,0.5);
  margin-bottom: 32px;
`;

const LoginBtn = styled.button`
  display: block;
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #01BCD6 0%, #0097a7 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(1, 188, 214, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(1, 188, 214, 0.4);
  }
`;

const LoadingText = styled.div`
  color: #01BCD6;
  font-size: 14px;
  margin-top: 16px;
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  a { color: #01BCD6; text-decoration: none; }
  &:hover { color: rgba(255,255,255,0.6); }
`;

export default function Login() {
  const [checking, setChecking] = useState(false);

  // 앤츠톡 팝업에서 SSO 토큰 수신
  useEffect(() => {
    const handleMessage = async (event) => {
      // 같은 계열(antsnest.kr / antsnest.co.kr)에서만 받는다.
      // 주소를 박아 두면 헤츠너(kr)에서 연 화면이 국내(co.kr) 메시지를
      // 기다리다 아무것도 못 받는다.
      if (!isTrustedOrigin(event.origin)) return;
      if (event.data?.type !== 'SSO_LOGIN_SUCCESS') return;

      const { token } = event.data;
      if (!token) return;

      setChecking(true);
      try {
        // Brand.AntsNest 백엔드에서 토큰 검증 + 세션 생성
        const res = await fetch('/api/sso/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (data.valid) {
          // 헤더(Navigator)가 user 상태를 useEffect로 한 번만 fetch 하므로
          // SPA navigate가 아닌 full reload로 이동하여 세션 쿠키 반영 보장
          window.location.replace('/Download');
        } else {
          setChecking(false);
        }
      } catch (err) {
        console.error('SSO 토큰 검증 실패:', err);
        setChecking(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // 세션 체크 (페이지 로드 시)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch('/api/check-session', { credentials: 'include' });
        if (res.ok) {
          window.location.href = '/Download';
        }
      } catch (err) {
        // 세션 없음 - 정상 상태
      }
    };
    checkSession();
  }, []);

  const handleLogin = () => {
    setChecking(true);
    const popupWidth = 480;
    const popupHeight = 780;
    const left = window.screenX + (window.outerWidth - popupWidth) / 2;
    const top = window.screenY + (window.outerHeight - popupHeight) / 2;

    // 지금 보고 있는 화면과 같은 계열의 앤츠톡을 연다. 헤츠너(kr)에서 국내
    // (co.kr) 를 열면 백엔드가 달라 로그인해도 이 화면의 세션이 안 생긴다.
    const popupUrl = `${siblingOrigin('talk')}/popup/login?parentOrigin=${encodeURIComponent(window.location.origin)}`;
    window.open(
      popupUrl,
      'talkLogin',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
  };

  return (
    <LoginWrapper>
      <LoginBox>
        <LogoImg src={Logo} alt="AntsNest" />
        <Title>앤츠네스트 로그인</Title>
        <SubTitle>인프라 관리를 위한 통합 플랫폼</SubTitle>

        <LoginBtn onClick={handleLogin} disabled={checking}>
          {checking ? '로그인 처리 중...' : '앤츠네스트 계정으로 로그인'}
        </LoginBtn>

        {checking && <LoadingText>로그인 후 창이 자동으로 닫힙니다...</LoadingText>}

        <LinkText>
          앤츠네스트 회원이 아니신가요?{' '}
          {/* 가입도 같은 계열에서 해야 한다. 헤츠너에서 국내로 보내면 DB 가
              달라 가입해도 이 화면에 로그인할 수 없다. */}
          <a href={siblingOrigin('talk')} target="_blank" rel="noopener noreferrer">
            {siblingOrigin('talk').replace('https://', '')}
          </a>
          {' '}에서 가입하세요.
        </LinkText>
      </LoginBox>
    </LoginWrapper>
  );
}